<template>
  <div id="serial-control">
    <h2 class="text-nowrap overflow-hidden">{{ translate('changeSerialHeading', labels, 'mymachine_AddSerialControl') }}:</h2>

    <div class="position-relative">
      <div v-if="disabled">
        <transition name="fade">
          <div
            v-if="!open"
            class="modal-backdrop modal-backdrop__partial"
          />
        </transition>

        <BBadge
          size="lg"
          class="d-inline-flex align-items-center justify-content-center cursor-pointer exposed"
          variant="primary"
          @click.prevent="openEditMode"
        >
          <b-icon
            icon="plus"
            class="small text-white"
          />
        </BBadge>

        <BLink
          class="font-condensed text-white ms-2 exposed"
          data-test-id="add-serial-link"
          @click.prevent="openEditMode"
        >
          {{ translate('addASerialNumber', labels, 'mymachine_AddSerialControl') }}
        </BLink>
      </div>

      <BButton
        v-else
        block
        variant="outline-light"
        class="dropdown-toggle text-start w-100"
        @click="showSerials"
      >
        <div class="w-100 text-truncate">{{ machine }}</div>
      </BButton>

      <transition name="fade">
        <div
          v-if="open"
          class="modal-backdrop"
          @click.prevent="() => { if (!loading) {open = false; editMode = false;} }"
        />
      </transition>

      <div
        v-if="open"
        class="serial-control-dropdown position-absolute w-100"
      >
        <b-list-group class="exposed bg-white rounded">
          <div
            v-if="getSerials"
            class="scrollable"
          >
            <b-list-group-item
              v-for="s in getSerials"
              :key="'list_serials_' + s.serial_number.toLowerCase() + '_' + s.active"
              :class="{'bg-gray-100':s.active}"
              class="p-2"
              @click.prevent="switchSerial(s.serial_number)"
            >
              <BLink
                class="font-condensed text-black"
                :disabled="s.active"
              >
                {{ s.serial_number }} <span v-if="s.model_id && s.model_id.length"> - {{ s.model_id }}</span>
              </BLink>

              <BLink
                class="text-gray"
                @click.stop="removeSerial(s)"
              >
                <b-icon
                  icon="trash"
                  class="ms-2 float-end"
                  :data-test-id="'serial-button-desktop__remove_' + s.serial_number"
                />
              </BLink>
            </b-list-group-item>
          </div>
          <b-list-group-item>
            <BRow v-if="editMode">
              <BCol>
                <BInputGroup>
                  <template #append>
                    <BInputGroup-text>
                      <BSpinner
                        v-if="loading"
                        label="Spinning"
                      />
                    </BInputGroup-text>
                  </template>

                  <BFormInput
                    ref="serialInput"
                    v-model="input.internalSerial"
                    data-test-id="add-serial-input"
                    autofocus
                    :placeholder="translatedLabel('enterSerialHere', 'mymachine_AddSerialControl')"
                    @keyup.enter.prevent="addSerial"
                    @input="handleInputInternalSerial"
                  />
                </BInputGroup>

                <transition name="fade">
                  <div
                    v-if="input.internalSerial && input.internalSerial.length && input.invalidSerial"
                    class="invalid-feedback d-block"
                  >
                    <b-icon
                      icon="exclamation-triangle"
                      class="me-1"
                    />
                    {{ formatErrorMessage(input.errorMessageLabel) }}
                  </div>
                </transition>

                <div class="d-lg-none mt-2">
                  <BButton
                    variant="primary"
                    block
                    class="w-100"
                    :disabled="loading || input.invalidSerial"
                    @click="addSerial"
                  >
                    <b-icon
                      icon="check"
                      class="me-1"
                    />
                    {{ translate('addButton', labels, 'mymachine_AddSerialControl') }}
                  </BButton>

                  <BButton
                    variant="outline-primary"
                    block
                    class="w-100"
                    @click="closeEditMode"
                  >
                    <b-icon
                      icon="times"
                      class="me-1"
                    />
                    {{ translate('cancelButton', labels, 'mymachine_AddSerialControl') }}
                  </BButton>
                </div>
              </BCol>

              <BCol
                class="d-none d-lg-block ps-0 pt-2"
                cols="auto"
              >
                <BLink
                  class="text-large mx-2"
                  :class="loading || input.invalidSerial ? 'text-gray' : 'text-success'"
                  :disabled="loading || input.invalidSerial"
                  @click="addSerial"
                >
                  <b-icon icon="check" />
                </BLink>
                <BLink
                  class="text-danger text-large mx-2"
                  @click="closeEditMode"
                >
                  <b-icon icon="times" />
                </BLink>
              </BCol>
            </BRow>

            <template v-else>
              <BBadge
                size="lg"
                class="d-inline-flex align-items-center text-white justify-content-center cursor-pointer exposed"
                variant="primary"
                @click.prevent="openEditMode"
              >
                <b-icon
                  icon="plus"
                  class="small"
                />
              </BBadge>

              <BLink
                class="font-condensed ms-2 exposed"
                data-test-id="add-serial-link__desktop"
                @click.prevent="openEditMode"
              >
                {{ translate('addASerialNumber', labels, 'mymachine_AddSerialControl') }}
              </BLink>
            </template>
          </b-list-group-item>
          <template v-if="getRegionalizedFeatures.searchByCustomer">
            <b-list-group-item class="p-0">
              <BLink
                v-b-tooltip="tooltipCustomerSearch"
                class="font-condensed exposed text-black d-flex p-25"
                data-test-id="add-customer-link__desktop"
                @click.prevent="openCustomerSerial"
              >
                <BBadge
                  size="lg"
                  class="d-inline-flex align-items-center justify-content-center cursor-pointer exposed flex-shrink-0"
                  variant="dark"
                >
                  <b-icon
                    icon="user"
                    class="small"
                  />
                </BBadge>
                <div class="ms-2">
                  {{ translatedLabel('formTitle', 'mymachine_customerSerialForm') }}
                </div>
              </BLink>
            </b-list-group-item>
          </template>
          <b-list-group-item class="p-0">
            <BLink
              v-b-tooltip="tooltipGenericTroubleshooting"
              class="font-condensed exposed text-black d-flex p-25"
              data-test-id="generic-troubleshoot-link__desktop"
              @click.prevent="openGenericTroubleshooting"
            >
              <BBadge
                size="lg"
                class="d-inline-flex align-items-center justify-content-center cursor-pointer exposed flex-shrink-0"
                variant="dark"
              >
                <b-icon
                  icon="wrench"
                  class="small"
                />
              </BBadge>
              <div class="ms-2">
                {{ translate('genericTroubleshooting', labels, 'mymachine_AddSerialControl') }}
              </div>
            </BLink>
          </b-list-group-item>
          <b-list-group-item
            v-if="noSerial"
            class="p-0"
          >
            <BLink
              class="font-condensed exposed text-black d-flex p-25"
              data-test-id="add-asset-link__desktop"
              @click.prevent="openNoSerial"
            >
              <BBadge
                size="lg"
                class="d-inline-flex align-items-center justify-content-center cursor-pointer exposed flex-shrink-0"
                variant="dark"
              >
                <b-icon
                  icon="info-circle"
                  class="small"
                />
              </BBadge>
              <div class="ms-2">
                {{ translate('addAsset', labels, 'mymachine_AddSerialControl') }}
              </div>
            </BLink>
          </b-list-group-item>
        </b-list-group>
      </div>
    </div>
    <BModal
      id="serial-modal"
      v-model="queryModalShow"
      centered
      lazy
      ok-only
      :no-footer="!queryError.present"
      no-header-close
      ok-title="Close"
    >
      <template #title>{{ translate(queryError.present ? 'errorModalTitle' : 'loadingModalTitle', labels, 'mymachine_AddSerialControl') }}</template>
      <div class="p-3 pb-4">
        <div>S/N: <span class="font-condensed">{{ querySerial }}</span></div>
        <div v-if="queryError.present">
          {{ formatErrorMessage(queryError.text) }}
        </div>
      </div>
    </BModal>
    <NoSerialForm
      ref="noSerialForm"
      @open-generic-form="openGenericTroubleshooting"
    />
    <template v-if="getRegionalizedFeatures.searchByCustomer">
      <CustomerSerialForm
        ref="customerSerialForm"
        @serial-number="selectSerialNumber"
      />
    </template>
    <GenericTroubleshoot ref="genericTroubleshoot" />
  </div>
</template>

<script setup lang="ts">
import {ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick} from 'vue'
import {useStore} from 'vuex'
import {useRoute, useRouter} from 'vue-router'
import {dpmmService} from '@/services'
import NoSerialForm from './NoSerialForm.vue'
import CustomerSerialForm from './CustomerSerialForm.vue'
import GenericTroubleshoot from './GenericTroubleshoot.vue'
import {useCommon} from '@/composables/useCommon'
import {capitalize} from '@dxp-common-components-updated/utils-global'
import constants from '@/constants'

const isDesktop = ref(window.innerWidth >= 992)
const isTablet = ref(window.innerWidth >= 768 && window.innerWidth < 992)

function onResize(): void {
  isDesktop.value = window.innerWidth >= 992
  isTablet.value = window.innerWidth >= 768 && window.innerWidth < 992
}

onMounted(() => window.addEventListener('resize', onResize))
onBeforeUnmount(() => window.removeEventListener('resize', onResize))

const {labels, translate, translatedLabel} = useCommon()
const store = useStore()
const route = useRoute()
const router = useRouter()

const props = withDefaults(defineProps<{
  disabled?: boolean
  noSerial?: boolean
}>(), {
  disabled: false,
  noSerial: false
})

const tooltipCustomerSearch = computed(() => ({
  placement: 'right',
  class: 'serial-custom-tooltip',
  title: translatedLabel('genericCustomerTooltip', 'mymachine_customerSerialForm'),
  delay: 200,
  noninteractive: true,
  disabled: !isDesktop.value && !isTablet.value,
  boundary: isDesktop.value ? 'viewport' : 'window'
}))

const tooltipGenericTroubleshooting = computed(() => ({
  placement: 'right',
  class: 'serial-custom-tooltip',
  title: translatedLabel('genericTroubleshootingTooltip', 'mymachine_AddSerialControl'),
  delay: 200,
  noninteractive: true,
  disabled: !isDesktop.value && !isTablet.value,
  boundary: isDesktop.value ? 'viewport' : 'window'
}))

const noSerialForm = ref<any>(null)
const customerSerialForm = ref<any>(null)
const genericTroubleshoot = ref<any>(null)
const serialInput = ref<any>(null)

const open = ref(false)
const editMode = ref(false)
const input = reactive<{
  invalidSerial: boolean
  internalSerial: string
  serialData: any
  errorMessageLabel: Record<string, any>
}>({
  invalidSerial: true,
  internalSerial: '',
  serialData: null,
  errorMessageLabel: {}
})
const queryModalShow = ref(false)
const querySerial = ref('')
const isModalOpen = ref(false)
const queryError = reactive<{ present: boolean; text: any }>({
  present: false,
  text: {}
})
const loading = ref(false)

const getRegion = computed<string>(() => store.getters['mymachine/getRegion'])
const getSerials = computed(() => store.getters['mymachine/getSerials'])
const getCurrentSerial = computed<string>(() => store.getters['mymachine/getCurrentSerial'])
const getCurrentModel = computed<string>(() => store.getters['mymachine/getCurrentModel'])
const getRegionalizedFeatures = computed(() => store.getters['mymachine/getRegionalizedFeatures'])
const getAppReady = computed<boolean>(() => store.getters['mymachine/getAppReady'])

const machine = computed<string>(() => {
  if (props.disabled || !getCurrentSerial.value) return ''
  return `${getCurrentSerial.value} - ${getCurrentModel.value}`
})

function replaceMessage(value: string, abbreviation?: string, secondAbbreviation?: string): string {
  const abbreviations = labels.value?.['mymachine_Abbreviation'] || {}
  const getReplacement = (key: string): string => abbreviations[key] ? abbreviations[key] : key
  const aliases = ['[product name]', '[region]']
  if (abbreviation && secondAbbreviation) {
    return value
      .replace('[firt region]', getReplacement(abbreviation))
      .replace('[second region]', getReplacement(secondAbbreviation))
  } else if (abbreviation) {
    const alias = aliases.find(a => value.includes(a))
    return alias ? value.replace(alias, getReplacement(abbreviation)) : value
  } else {
    return value
  }
}

function formatErrorMessage(errorLabel: Record<string, any>): string {
  if (!errorLabel || !errorLabel.ck) return ''
  let msg = translate(errorLabel.ck, labels.value, errorLabel.cp)
  msg = capitalize(msg)
  msg = replaceMessage(msg, errorLabel.r1, errorLabel.r2)
  return msg
}

watch(getAppReady, (n) => {
  if (n) handleSNFromQuery()
})

watch(getRegion, async (region) => {
  await updateQuery(getCurrentSerial.value, region)
  clearField()
  // If serial is present in URL, retry lookup once region becomes available.
  if (region && route.query.serial) {
    handleSNFromQuery()
  }
})

onMounted(() => {
  if (getAppReady.value) handleSNFromQuery()
})

function selectSerialNumber(serial: any): void {
  input.serialData = serial
  input.internalSerial = input.serialData.serial_number
  isModalOpen.value = true
  addSerial()
}

function handleSNFromQuery(): void {
  if (route.path.endsWith('/implicit/callback')) return
  const serialFromQuery = route.query.serial as string
  if (serialFromQuery) {
    handleQuerySerial(serialFromQuery.toUpperCase())
  }
}

function isNewSerial(sn: string): boolean | undefined {
  if (sn && router.currentRoute.value.path !== '/' && getSerials.value?.length) {
    return !getSerials.value.find((item: any) => item.serial_number.toUpperCase() === sn.toUpperCase())
  }
}

function handleQuerySerial(sn: string): void {
  if (sn && sn.length) {
    let queryTimeout = setTimeout(() => { queryModalShow.value = true }, 600)
    querySerial.value = sn

    fetchDetailsWithRegionFallback(sn).then(({data, region}: any) => {
      if (data.serial_number === sn) {
        store.commit('mymachine/saveSNErrorStatus', false)
        if (getRegion.value !== region) {
          store.commit('mymachine/saveRegion', {region, redirectToDashboard: false})
        }
        store.commit('mymachine/addSerialNumber', data)
        setActiveSerial(data)
        clearTimeout(queryTimeout)
        queryModalShow.value = false
      } else {
        queryError.present = true
        queryError.text = {ck: 'defaultError', cp: 'shared_CommonErrors'}
        setActiveSerial()
      }
    }).catch((e: any) => {
      store.commit('mymachine/saveSNErrorStatus', true)
      queryError.present = true
      queryError.text = getErrorMessage(e)
      setActiveSerial()
    })
  }
}

function addSerial(): void {
  formatInput()
  validateSerial().then(() => {
    if (!input.invalidSerial) {
      const isNew = isNewSerial(input.serialData.serial_number)
      store.commit('mymachine/addSerialNumber', input.serialData)
      if (isNew) {
        router.push({path: '/', query: {serial: input.serialData.serial_number, [constants.REGION_QUERY_NAME]: getRegion.value}})
      } else {
        updateQuery(input.serialData.serial_number)
      }
      clearField()
      editMode.value = false
      open.value = false
    }
    loading.value = false
    if (isModalOpen.value) {
      customerSerialForm.value.loading = false
      isModalOpen.value = false
      customerSerialForm.value.closeModal()
    }
  })
}

async function updateQuery(s: any, region?: string): void {
  let sn = s && s.serial_number ? s.serial_number : s

  if (!route.query.serial || route.query.serial !== sn || !route.query[constants.REGION_QUERY_NAME]) {
    const regionFromQuery = route.query[constants.REGION_QUERY_NAME] as string
    const query: Record<string, any> = {...route.query}
    query.serial = sn || undefined
    query[constants.REGION_QUERY_NAME] = region || regionFromQuery || getRegion.value

    const navigation = region
      ? {
        name: constants.DASHBOARD,
        query
      }
      : {
        ...router.currentRoute.value,
        query
      }

    await router.replace(navigation)
  }
}

function removeSerial(sn: any): void {
  store.commit('mymachine/saveSNErrorStatus', false)
  store.commit('mymachine/removeSerialNumber', sn)
  updateQuery(getCurrentSerial.value)
}

function switchSerial(serialNumber: string): void {
  if (!serialNumber) return
  open.value = false
  editMode.value = false
  fetchDetailsWithRegionFallback(serialNumber).then(({data, region}: any) => {
    if (getRegion.value !== region) {
      store.commit('mymachine/saveRegion', {region, redirectToDashboard: false})
    }
    if (data.serial_number !== serialNumber) {
      store.commit('mymachine/removeSerialNumber', serialNumber)
      updateQuery(getCurrentSerial.value)
    }
  }).catch(() => {
    store.commit('mymachine/removeSerialNumber', serialNumber)
    updateQuery(getCurrentSerial.value)
  })
  store.commit('mymachine/activateSerialNumber', serialNumber)
  if (route.query.serial !== serialNumber) updateQuery(serialNumber)
}

function setActiveSerial(s: any = getCurrentSerial.value): void {
  if (s) {
    open.value = false
    editMode.value = false
    store.commit('mymachine/activateSerialNumber', s)
  }
  if (route.query.serial !== s) updateQuery(s)
}

async function validateSerial(_s: string = input.internalSerial): Promise<void> {
  _s = _s.trim().toUpperCase()
  if (!_s.match(/^([A-Z0-9]{4}[0-9]{5}|[A-Z0-9]{4}[0-9*][0-9]{7}|[A-Z0-9]{4}[0-9]{6})$/)) {
    input.invalidSerial = true
    input.errorMessageLabel = {ck: 'wrongSerialNumberFormat', cp: 'mymachine_AddSerialControl'}
    return new Promise(() => {})
  }

  loading.value = true
  input.invalidSerial = false

  try {
    const response = await fetchDetailsWithRegionFallback(_s)
    if (response.data.serial_number.toUpperCase() !== _s) {
      input.invalidSerial = true
    } else {
      input.invalidSerial = false
      input.errorMessageLabel = {}
      input.serialData = response.data

      // Keep app state aligned with the region that actually resolved this serial.
      if (getRegion.value !== response.region) {
        store.commit('mymachine/saveRegion', {region: response.region, redirectToDashboard: false})
      }
    }
  } catch (error: any) {
    input.invalidSerial = true
    input.errorMessageLabel = getErrorMessage(error)
  } finally {
    loading.value = false
  }
}

async function fetchDetailsWithRegionFallback(serialNumber: string): Promise<{data: any; region: string}> {
  const regionFromQuery = route.query[constants.REGION_QUERY_NAME] as string
  const regionFromStore = getRegion.value

  const candidates = new Set<string>()
  if (regionFromQuery) candidates.add(regionFromQuery.toUpperCase())
  if (regionFromStore) candidates.add(regionFromStore.toUpperCase())

  if (candidates.size === 0) {
    try {
      const {data} = await dpmmService.fetchAvailableRegions()
      const available = Array.isArray(data?.business_units) ? data.business_units : []
      available.forEach((r: string) => candidates.add((r || '').toUpperCase()))
    } catch {
      // Fallback list when available regions cannot be fetched.
      ['NA', 'EMEA', 'ALAO'].forEach((r) => candidates.add(r))
    }
  }

  let lastError: any = null
  for (const region of candidates) {
    try {
      const response = await dpmmService.fetchDetails(serialNumber, region)
      return {data: response.data, region}
    } catch (e: any) {
      lastError = e
      const code = Number(e?.code)
      const message = String(e?.message || '').toLowerCase()
      const isBusinessUnitMismatch =
        (code === 400 && message.includes('business unit')) ||
        (code === 403 && (message.includes('business unit') || message.includes('can not access') || message.includes('not authorized to access')))

      if (isBusinessUnitMismatch) continue
      throw e
    }
  }

  throw lastError || {code: 400, message: 'Invalid business unit'}
}

function getErrorMessage(error: any): Record<string, any> {
  const {unathorizeType, replacement} = getUnathorizeError(error.message)
  const errorMessages: Record<string, any> = {
    default: {ck: 'defaultError', cp: 'shared_CommonErrors'},
    invalidBusinessUnit: {ck: 'invalidBusinessUnit', cp: 'mymachine_AddSerialControl'},
    wrongSerialNumberFormat: {ck: 'wrongSerialNumberFormat', cp: 'mymachine_AddSerialControl'},
    unathorized: {
      default: {ck: 'unauthorizedAccess', cp: 'mymachine_AddSerialControl'},
      stolen: {ck: 'stolenMachine', cp: 'mymachine_AddSerialControl'},
      product: {ck: 'unauthorizedAccessProduct', cp: 'mymachine_AddSerialControl', r1: replacement[1] || replacement[2]},
      business: {ck: 'unauthorizedAccessBusiness', cp: 'mymachine_AddSerialControl', r1: replacement[1]},
      region: {ck: 'unauthorizedAccessRegion', cp: 'mymachine_AddSerialControl', r1: replacement[1], r2: replacement[2]}
    },
    noSerialFound: {ck: 'noSerialFound', cp: 'shared_CommonErrors'}
  }
  let message = errorMessages.default

  if (error.code) switch (error.code) {
  case 400:
    if (error.message.startsWith('businessUnit') || error.message === 'Invalid business unit') message = errorMessages.invalidBusinessUnit
    else message = errorMessages.wrongSerialNumberFormat
    break
  case 403:
    message = errorMessages.unathorized[unathorizeType]
    break
  case 404:
    message = errorMessages.noSerialFound
    break
  }
  return message
}

function getUnathorizeError(message: string): { unathorizeType: string; replacement: any[] } {
  const stolen = message.match(/stolen/)
  if (stolen) return {unathorizeType: 'stolen', replacement: []}

  const product = message.match(/access (.*) machine|access (.*) product/)
  if (product) return {unathorizeType: 'product', replacement: product}

  const region = message.match(/access (.*) serial in (.*) business unit/)
  if (region) return {unathorizeType: 'region', replacement: region}

  const business = message.match(/access (.*) business unit/)
  if (business) return {unathorizeType: 'business', replacement: business}

  return {unathorizeType: 'default', replacement: []}
}

function clearField(): void {
  editMode.value = false
  input.invalidSerial = true
  input.internalSerial = ''
  input.serialData = null
  input.errorMessageLabel = {}
}

function openEditMode(): void {
  open.value = true
  editMode.value = true
}

function openGenericTroubleshooting(): void {
  open.value = false
  editMode.value = false
  genericTroubleshoot.value?.openModal()
}

function openNoSerial(): void {
  open.value = false
  editMode.value = false
  noSerialForm.value?.openModal()
}

function openCustomerSerial(): void {
  open.value = false
  editMode.value = false
  customerSerialForm.value?.openModal()
}

function closeEditMode(): void {
  open.value = !props.disabled
  editMode.value = false
}

function showSerials(): void {
  open.value = true
}

function formatInput(): void {
  input.internalSerial = input.internalSerial
    .replace(/\s/g, '')
    .toUpperCase()
}

function handleInputInternalSerial(e: Event): void {
  const target = e.target as HTMLInputElement
  const isSpaceInValue = target.value.includes(' ')
  const savedSelectionStart = (target.selectionStart || 0) - Number(isSpaceInValue)

  formatInput()
  validateSerial()

  nextTick(() => {
    target.selectionStart = target.selectionEnd = savedSelectionStart
  })
}
</script>

<style lang="scss" scoped>
@import '../../scss/colors';
@import '../../scss/z-index';
@import '../../scss/grid';

@include media-breakpoint-up('sm') {
  #serial-modal .modal-dialog {
    max-width: 75%;
  }
}

@include media-breakpoint-up('md') {
  #serial-modal .modal-dialog {
    max-width: 50%;
  }
}

#serial-control {
  .badge {
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    font-size: 1.2rem;
    box-sizing: content-box;
  }
}

.serial-control-dropdown {
  width: 100%;
  top: 0;

  .list-group-item{
    padding: .75rem;

    .p-25 {
      padding: .75rem;
    }

    > a:hover {
      color: inherit;
      text-decoration: none;
    }

    &:not(:first-child):hover {
      background: $tetriary;
    }

    .disabled {
      cursor: default !important;
      color: $black;

      &:hover {
        text-decoration: none;
      }
    }
  }

  .scrollable {
    max-height: 50vh;
    overflow-y: auto;
  }

  .input-group {
    .input-group-text {
      padding: 0;
      font-size: 22px;
      width: 2.7rem;
      align-items: center;
      justify-content: center;
    }

    input {
      border-right: none;
    }
  }
}

.exposed {
  position: relative;
  z-index: $zindex-modal-backdrop + 1;
}

.modal-backdrop {
  opacity: .6;
}

.modal-backdrop__partial {
  z-index: 100 !important;

  & ~ .exposed {
    z-index: 101;
  }
}

.btn-outline-primary {
  border: none;
}

:deep(.serial-custom-tooltip) {
  .tooltip-inner {
    background-color: $black !important;
    color: $white !important;
  }

  &.bs-tooltip-end .tooltip-arrow::before {
    border-right-color: $black;
  }
}
</style>
